// Tiered take-profit helpers — frontend mirror of `plugins/common.rs`'s
// `parse_tiers` / `next_session_date_et` / `pick_take_profit_pct`. Used to
// surface a calculated buyback target price on the Open Positions panes
// when no live BUY_TO_CLOSE / VERTICAL_SPREAD_CLOSE order exists.
//
// A tier's threshold is one of two trigger kinds, mirroring the backend syntax
// "t1:p1,t2:p2,...":
//   - "<n>"   → days-SINCE-ENTRY tier; fires once age reaches n (e.g. "3:75")
//   - "<n>D"  → days-to-expiration tier; fires once DTE is n OR LESS ("2D:90")
// The bare-number form is the original age tier and stays backward compatible.

export type TierTrigger = { kind: "age"; n: number } | { kind: "dte"; n: number };
export type Tier = { trigger: TierTrigger; pct: number };

export function parseTiers(raw: string | undefined | null): Tier[] {
  if (!raw) return [];
  const out: Tier[] = [];
  for (const part of raw.split(",")) {
    const s = part.trim();
    if (!s) continue;
    const [spec, p] = s.split(":");
    const pn = parseFloat(p);
    if (!Number.isFinite(pn)) continue;
    const specT = (spec ?? "").trim();
    const dteMatch = /^(-?\d+)\s*[Dd]$/.exec(specT);
    if (dteMatch) {
      const n = parseInt(dteMatch[1], 10);
      if (Number.isFinite(n)) out.push({ trigger: { kind: "dte", n }, pct: pn });
    } else {
      const n = parseInt(specT, 10);
      if (Number.isFinite(n)) out.push({ trigger: { kind: "age", n }, pct: pn });
    }
  }
  return out;
}

function nextSessionDateEt(): Date {
  const nowEt = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
  const d = new Date(nowEt.getFullYear(), nowEt.getMonth(), nowEt.getDate());
  while (d.getDay() === 0 || d.getDay() === 6) {
    d.setDate(d.getDate() + 1);
  }
  return d;
}

// Parse a "YYYY-MM-DD" (or Date) expiration into a local midnight Date, or null.
function toExpirationDate(expiration: string | Date | null | undefined): Date | null {
  if (!expiration) return null;
  if (expiration instanceof Date) {
    return Number.isNaN(expiration.getTime())
      ? null
      : new Date(expiration.getFullYear(), expiration.getMonth(), expiration.getDate());
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(expiration.trim());
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

// Returns decay (0..1). Mirrors `pick_take_profit_pct`: among every tier whose
// trigger currently qualifies — an age tier when days-since-entry >= n, a DTE
// tier when days-to-expiration <= n — pick the MOST AGGRESSIVE (highest decay).
// If none qualify, fall back to the LEAST aggressive tier so a position is never
// closed early. Both age and DTE are measured against the next trading session.
// DTE tiers are skipped when no expiration is supplied (they can't be evaluated).
export function pickTakeProfitPct(
  openedAt: string | Date,
  tiersRaw: string | undefined | null,
  expiration?: string | Date | null,
  fallbackPct = 0.75,
): number {
  const tiers = parseTiers(tiersRaw);
  if (tiers.length === 0) return fallbackPct;
  const opened = new Date(openedAt);
  const openedEt = new Date(opened.toLocaleString("en-US", { timeZone: "America/New_York" }));
  const openedDate = new Date(openedEt.getFullYear(), openedEt.getMonth(), openedEt.getDate());
  const session = nextSessionDateEt();
  const days = Math.max(0, Math.round((session.getTime() - openedDate.getTime()) / 86400000));
  const exp = toExpirationDate(expiration);
  const dte = exp == null ? null : Math.round((exp.getTime() - session.getTime()) / 86400000);

  const qualifies = (t: Tier): boolean =>
    t.trigger.kind === "age"
      ? days >= t.trigger.n
      : dte != null && dte <= t.trigger.n;

  const qualifying = tiers.filter(qualifies);
  const pool = qualifying.length > 0 ? qualifying : tiers;
  // Most aggressive when something qualifies; least aggressive as the fallback.
  const chosen = qualifying.length > 0
    ? pool.reduce((m, t) => (t.pct > m.pct ? t : m))
    : pool.reduce((m, t) => (t.pct < m.pct ? t : m));
  return chosen.pct / 100;
}

// premium/credit × (1 − decay), rounded to cents. Returns null on invalid input.
export function calcBuybackTarget(
  entryPrice: number | null | undefined,
  openedAt: string | Date | null | undefined,
  tiersRaw: string | undefined | null,
  expiration?: string | Date | null,
): number | null {
  if (entryPrice == null || !Number.isFinite(entryPrice) || entryPrice <= 0) return null;
  if (!openedAt) return null;
  const pct = pickTakeProfitPct(openedAt, tiersRaw, expiration);
  return Math.round(entryPrice * (1 - pct) * 100) / 100;
}
